import React from 'react'
import { Session, Room } from '@/types'
import { useSwipeGesture } from '@/hooks/useSwipeGesture'
import MobileDayNavigation from './MobileDayNavigation'
import MobileRoomCard from './MobileRoomCard'

// Helper to format date as YYYY-MM-DD for comparison
function formatDateKey(date: Date): string {
  return date.toISOString().split('T')[0]
}

// Check if date is in the past
function isPastDate(date: Date): boolean {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const compareDate = new Date(date)
  compareDate.setHours(0, 0, 0, 0)
  return compareDate < today
}

// Check if date is in the future
function isFutureDate(date: Date): boolean {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const compareDate = new Date(date)
  compareDate.setHours(0, 0, 0, 0)
  return compareDate > today
}

interface SectionSummary {
  section_id: number | null
  section_name: string
  image_count: number
  detection_count: number
  status: 'no_upload' | 'not_validated' | 'validated'
}

interface SessionSummary {
  session_id: number
  sections: SectionSummary[]
  total_detections: number
  overall_status: 'not_started' | 'incomplete_sections' | 'awaiting_validation' | 'complete'
}

interface MobileDayViewProps {
  selectedDate: Date
  rooms: Room[]
  sessionsByRoomAndDate: Map<string, Session>
  sessionSummaries: Record<number, SessionSummary>
  onPreviousDay: () => void
  onNextDay: () => void
  onToday: () => void
  onSessionClick: (session: Session) => void
  onCreateSession: (roomId: number, date: Date) => void
  onSessionLongPress?: (session: Session) => void
  selectionMode?: boolean
  selectedCells?: Set<string>
  onToggleCellSelection?: (roomId: number, dateKey: string) => void
}

/**
 * Main mobile view for sessions - shows rooms as cards for a single day
 * Supports swipe gestures to navigate between days
 */
const MobileDayView: React.FC<MobileDayViewProps> = ({
  selectedDate,
  rooms,
  sessionsByRoomAndDate,
  sessionSummaries,
  onPreviousDay,
  onNextDay,
  onToday,
  onSessionClick,
  onCreateSession,
  onSessionLongPress,
  selectionMode,
  selectedCells,
  onToggleCellSelection,
}) => {
  // Set up swipe gestures
  const swipeRef = useSwipeGesture({
    onSwipeLeft: onNextDay, // Swipe left = go to next day
    onSwipeRight: onPreviousDay, // Swipe right = go to previous day
  })

  const dateKey = formatDateKey(selectedDate)
  const isPast = isPastDate(selectedDate)
  const isFuture = isFutureDate(selectedDate)
  const isSunday = selectedDate.getDay() === 0

  // Get session for a room on the selected date
  const getSession = (roomId: number): Session | undefined => {
    const key = `${roomId}-${dateKey}`
    return sessionsByRoomAndDate.get(key)
  }

  // Get summary for a session
  const getSummary = (session: Session | undefined): SessionSummary | undefined => {
    if (!session) return undefined
    return sessionSummaries[session.id]
  }

  return (
    <div className="flex flex-col h-full">
      {/* Day Navigation */}
      <MobileDayNavigation
        selectedDate={selectedDate}
        onPrevious={onPreviousDay}
        onNext={onNextDay}
        onToday={onToday}
      />

      {/* Swipeable Content Area */}
      <div
        ref={swipeRef}
        className="flex-1 overflow-y-auto px-4 py-4 space-y-4"
      >
        {rooms.length === 0 ? (
          <div className="flex items-center justify-center h-48 text-gray-500">
            <p className="text-center">
              No rooms available.<br />
              Create rooms in the admin panel.
            </p>
          </div>
        ) : (
          rooms.map((room) => {
            const session = getSession(room.id)
            const summary = getSummary(session)
            const cellKey = `${room.id}-${dateKey}`
            const isSelected = selectedCells?.has(cellKey) ?? false

            return (
              <MobileRoomCard
                key={room.id}
                room={room}
                session={session}
                summary={summary}
                date={selectedDate}
                isPast={isPast}
                isFuture={isFuture}
                isSunday={isSunday}
                isSelected={isSelected}
                selectionMode={selectionMode}
                onSessionClick={() => {
                  if (session) {
                    onSessionClick(session)
                  }
                }}
                onCreateSession={() => {
                  onCreateSession(room.id, selectedDate)
                }}
                onToggleSelection={() => {
                  if (onToggleCellSelection) {
                    onToggleCellSelection(room.id, dateKey)
                  }
                }}
                onSessionLongPress={onSessionLongPress}
              />
            )
          })
        )}
      </div>
    </div>
  )
}

export default MobileDayView
